import type { ChangeEvent } from 'react';
import type { BadgeDataKey, BadgeElement } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Button, CheckboxField, SelectField, TextField } from '../../components/ui';
import { DATA_KEYS } from './layout';

export interface FieldChoice {
  key: string;
  label: string;
  starred: boolean;
}

const num = (e: ChangeEvent<HTMLInputElement>): number | null => {
  const v = e.target.valueAsNumber;
  return Number.isFinite(v) ? v : null;
};

function Num({
  label,
  value,
  onValue,
  min,
  max,
  step = 0.5,
  disabled,
}: {
  label: string;
  value: number;
  onValue: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled: boolean;
}) {
  return (
    <TextField
      label={label}
      type="number"
      inputMode="decimal"
      value={value}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => {
        const n = num(e);
        if (n !== null) onValue(n);
      }}
    />
  );
}

/** Settings of the selected element. `patch` merges changes; geometry is snapped by the caller. */
export function Properties({
  element,
  fields,
  canEdit,
  onPatch,
  onRemove,
}: {
  element: BadgeElement;
  fields: FieldChoice[];
  canEdit: boolean;
  onPatch: (patch: Partial<BadgeElement>) => void;
  onRemove: () => void;
}) {
  const off = !canEdit;
  const current = element.type === 'text' && element.source.kind === 'data' ? element.source.key : null;
  const knownField = current !== null && fields.some((f) => f.key === current);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Num label={t('badgeUi.prop.x')} value={element.x} min={0} max={200} onValue={(x) => onPatch({ x })} disabled={off} />
        <Num label={t('badgeUi.prop.y')} value={element.y} min={0} max={200} onValue={(y) => onPatch({ y })} disabled={off} />
        <Num label={t('badgeUi.prop.w')} value={element.w} min={1} max={200} onValue={(w) => onPatch({ w })} disabled={off} />
        <Num label={t('badgeUi.prop.h')} value={element.h} min={1} max={200} onValue={(h) => onPatch({ h })} disabled={off} />
      </div>

      {element.type === 'text' ? (
        <>
          <SelectField
            label={t('badgeUi.prop.sourceKind')}
            value={element.source.kind}
            disabled={off}
            onChange={(e) =>
              onPatch({
                source: e.target.value === 'static' ? { kind: 'static', text: t('badgeUi.create.starterText') } : { kind: 'data', key: 'visitor_name' },
              })
            }
          >
            <option value="static">{t('badgeUi.prop.sourceStatic')}</option>
            <option value="data">{t('badgeUi.prop.sourceData')}</option>
          </SelectField>
          {element.source.kind === 'static' ? (
            <TextField
              label={t('badgeUi.prop.text')}
              value={element.source.text}
              maxLength={200}
              disabled={off}
              onChange={(e) => onPatch({ source: { kind: 'static', text: e.target.value } })}
            />
          ) : (
            <SelectField
              label={t('badgeUi.prop.dataKey')}
              value={element.source.key}
              disabled={off}
              onChange={(e) => onPatch({ source: { kind: 'data', key: e.target.value as BadgeDataKey } })}
            >
              {DATA_KEYS.map((k) => (
                <option key={k} value={k}>
                  {t(`badgeUi.data.${k}`)}
                </option>
              ))}
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {t(f.starred ? 'badgeUi.data.fieldStarred' : 'badgeUi.data.field', { label: f.label })}
                </option>
              ))}
              {current && current.startsWith('field:') && !knownField ? <option value={current}>{t('badgeUi.data.fieldOther')}</option> : null}
            </SelectField>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Num label={t('badgeUi.prop.fontSize')} value={element.fontSize} min={4} max={96} step={1} onValue={(fontSize) => onPatch({ fontSize })} disabled={off} />
            <Num label={t('badgeUi.prop.maxLines')} value={element.maxLines} min={1} max={4} step={1} onValue={(maxLines) => onPatch({ maxLines: Math.round(maxLines) })} disabled={off} />
          </div>
          <SelectField label={t('badgeUi.prop.align')} value={element.align} disabled={off} onChange={(e) => onPatch({ align: e.target.value as 'left' | 'center' | 'right' })}>
            <option value="left">{t('badgeUi.align.left')}</option>
            <option value="center">{t('badgeUi.align.center')}</option>
            <option value="right">{t('badgeUi.align.right')}</option>
          </SelectField>
          <CheckboxField label={t('badgeUi.prop.bold')} checked={element.bold} disabled={off} onChange={(e) => onPatch({ bold: e.target.checked })} />
          <CheckboxField label={t('badgeUi.prop.shrink')} checked={element.shrinkToFit} disabled={off} onChange={(e) => onPatch({ shrinkToFit: e.target.checked })} />
        </>
      ) : null}

      {element.type === 'image' ? (
        <>
          <SelectField
            label={t('badgeUi.prop.image')}
            value={element.source}
            disabled={off}
            onChange={(e) => onPatch({ source: e.target.value as 'photo' | 'tenant_logo' | 'site_logo' | 'template_logo' })}
          >
            <option value="photo">{t('badgeUi.image.photo')}</option>
            <option value="tenant_logo">{t('badgeUi.image.tenant_logo')}</option>
            <option value="site_logo">{t('badgeUi.image.site_logo')}</option>
            <option value="template_logo">{t('badgeUi.image.template_logo')}</option>
          </SelectField>
          <SelectField label={t('badgeUi.prop.fit')} value={element.fit} disabled={off} onChange={(e) => onPatch({ fit: e.target.value as 'contain' | 'cover' })}>
            <option value="contain">{t('badgeUi.fit.contain')}</option>
            <option value="cover">{t('badgeUi.fit.cover')}</option>
          </SelectField>
        </>
      ) : null}

      {element.type === 'box' ? (
        <>
          <Num label={t('badgeUi.prop.stroke')} value={element.strokeMm} min={0} max={5} step={0.1} onValue={(strokeMm) => onPatch({ strokeMm })} disabled={off} />
          <CheckboxField label={t('badgeUi.prop.fill')} checked={element.fill} disabled={off} onChange={(e) => onPatch({ fill: e.target.checked })} />
        </>
      ) : null}

      {canEdit ? (
        <Button variant="danger" onClick={onRemove}>
          {t('badgeUi.remove')}
        </Button>
      ) : null}
    </div>
  );
}
